import { useNavigate } from "@tanstack/react-router"
import { useEffect, useRef, useState } from "react"
import { ConnectClaudeDialog } from "@/components/common/connect-claude-dialog"
import { isCsvFile } from "@/lib/is-csv-file"
import { useSetSharedFile } from "@/store/selectors"
import { useUIStore } from "@/store/ui"

/**
 * Opt-out attribute for the window-level drop listener. Any element carrying
 * it (and everything inside it) handles its own drops — an in-page drop zone
 * such as first-import-choices.tsx's upload row, or any future one. Dialog
 * and drawer content opt out implicitly.
 */
const NO_GLOBAL_DROP_ATTR = "data-no-global-drop"

const OPT_OUT_SELECTOR = `[data-slot="dialog-content"], [data-slot="drawer-content"], [${NO_GLOBAL_DROP_ATTR}]`

export function isOptedOutTarget(target: EventTarget | null): boolean {
  // e.target is only guaranteed to be an Element for events that landed on
  // real page content; guard defensively rather than assume (found via a
  // synthetic-event test that dispatched straight on window).
  if (!(target instanceof Element)) return false
  return !!target.closest(OPT_OUT_SELECTOR)
}

export function hasFiles(e: Pick<DragEvent, "dataTransfer">): boolean {
  // `types` is a DOMStringList in some engines (no .includes), so normalise.
  const types = e.dataTransfer?.types
  return !!types && Array.from(types as ArrayLike<string>).includes("Files")
}

// How long after the last dragover the overlay stays up. Browsers fire
// dragover every ~50ms while a drag is over the page, so a short quiet period
// means the drag left the window (or was cancelled) even when no matching
// dragleave/dragend ever arrives.
export const DRAG_IDLE_MS = 150

/**
 * Every route accepts a dropped file (PAISA-ING proposal #2, "drop-anywhere").
 * A CSV hands off to the existing import dialog via the same pendingAction /
 * sharedFile pattern the share_target landing page uses; anything else routes
 * to Connect Claude with a clear message instead of failing silently. A drop
 * that lands inside an already-open dialog (e.g. the statement Vault's own
 * drop zone) is left alone so this listener never double-handles it.
 */
export function GlobalDropZone() {
  const [dragActive, setDragActive] = useState(false)
  const [claudeOpen, setClaudeOpen] = useState(false)
  const [claudeContext, setClaudeContext] = useState<string | undefined>(undefined)
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const navigate = useNavigate()
  const setSharedFile = useSetSharedFile()
  const setPendingAction = useUIStore((s) => s.setPendingAction)

  useEffect(() => {
    // No enter/leave depth counter: enter/leave pairs desync across nested
    // elements, iframes and the window edge, which made the overlay vanish
    // mid-drag. Instead the overlay is "on while dragover keeps firing":
    // drop/dragend turn it off at once, and leaving the window (no more
    // dragover) turns it off after DRAG_IDLE_MS. dragleave is ignored — its
    // relatedTarget is unreliable across engines.
    function clearIdle() {
      if (idleTimer.current) clearTimeout(idleTimer.current)
      idleTimer.current = null
    }
    function hide() {
      clearIdle()
      setDragActive(false)
    }
    function show() {
      clearIdle()
      setDragActive(true)
      idleTimer.current = setTimeout(hide, DRAG_IDLE_MS)
    }
    function onDragEnter(e: DragEvent) {
      if (!hasFiles(e)) return
      if (isOptedOutTarget(e.target)) hide()
      else show()
    }
    function onDragOver(e: DragEvent) {
      if (!hasFiles(e)) return
      // Always prevent default when files are involved so the browser never
      // navigates away to display the dropped file, even over a dialog's own
      // drop zone (which still needs the drop event to reach it).
      e.preventDefault()
      if (isOptedOutTarget(e.target)) hide()
      else show()
    }
    function onDragEnd() {
      hide()
    }
    function onDrop(e: DragEvent) {
      hide()
      if (!hasFiles(e)) return
      if (isOptedOutTarget(e.target)) return // let the dialog's own handler take it

      e.preventDefault()
      const file = e.dataTransfer?.files?.[0]
      if (!file) return

      if (isCsvFile(file)) {
        setSharedFile(file)
        setPendingAction("csv_import")
        navigate({ to: "/transactions" })
        return
      }

      setClaudeContext(
        `"${file.name}" isn't a CSV — PDF and other bank statements import via Claude.`,
      )
      setClaudeOpen(true)
    }

    window.addEventListener("dragenter", onDragEnter)
    window.addEventListener("dragover", onDragOver)
    window.addEventListener("drop", onDrop)
    window.addEventListener("dragend", onDragEnd)
    return () => {
      clearIdle()
      window.removeEventListener("dragend", onDragEnd)
      window.removeEventListener("dragenter", onDragEnter)
      window.removeEventListener("dragover", onDragOver)
      window.removeEventListener("drop", onDrop)
    }
  }, [navigate, setSharedFile, setPendingAction])

  return (
    <>
      {dragActive && (
        <div
          className="pointer-events-none fixed inset-0 z-[100] flex items-center justify-center bg-bg-base/85 backdrop-blur-sm"
          data-testid="global-drop-overlay"
        >
          <div className="rounded-2xl border-2 border-dashed border-accent-green bg-bg-surface px-8 py-6 text-center shadow-lg">
            <p className="text-callout font-semibold text-text-primary">Drop to import</p>
            <p className="mt-1 text-caption text-text-muted">
              CSV imports here · everything else goes to Connect Claude
            </p>
          </div>
        </div>
      )}
      <ConnectClaudeDialog
        open={claudeOpen}
        onClose={() => setClaudeOpen(false)}
        context={claudeContext}
      />
    </>
  )
}
